import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { PasswordField, FormError, FormSuccess, SubmitButton } from '@/components/auth/FormFields';
import { authApi, readApiError } from '@/lib/auth-api';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(
    token ? null : (t('auth.error_missing_token', { defaultValue: 'Reset link is missing or invalid.' }) as string),
  );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (pw.length < 8) {
      setErr(t('auth.error_password_too_short', { defaultValue: 'Password must be at least 8 characters.' }) as string);
      return;
    }
    if (pw !== pw2) {
      setErr(t('auth.error_password_mismatch', { defaultValue: 'Passwords do not match.' }) as string);
      return;
    }
    setPending(true);
    try {
      await authApi.resetPassword(token, pw);
      setDone(true);
      setTimeout(() => nav('/login', { replace: true }), 1800);
    } catch (caught) {
      setErr(readApiError(caught, t('auth.error_reset_failed', { defaultValue: 'Could not reset password. The link may have expired.' }) as string));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthLayout
      title={t('auth.reset_title', { defaultValue: 'Set a new password.' })}
      subtitle={
        done
          ? t('auth.reset_done_subtitle', { defaultValue: 'Password updated. Signing you in…' }) as string
          : t('auth.reset_subtitle', { defaultValue: 'Pick something memorable — but tough to guess.' }) as string
      }
      footer={
        <span>
          <Link to="/login" className="text-accent-700 font-semibold hover:underline">
            ← {t('auth.back_to_login', { defaultValue: 'Back to sign in' })}
          </Link>
        </span>
      }
    >
      {done ? (
        <FormSuccess
          message={t('auth.reset_success', { defaultValue: 'Your password has been updated.' }) as string}
        />
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <FormError message={err} />
          <PasswordField
            id="pw"
            label={t('auth.new_password_label', { defaultValue: 'New password' }) as string}
            value={pw}
            onChange={setPw}
            autoComplete="new-password"
            showStrength
            required
            disabled={!token}
          />
          <PasswordField
            id="pw2"
            label={t('auth.confirm_password_label', { defaultValue: 'Confirm password' }) as string}
            value={pw2}
            onChange={setPw2}
            autoComplete="new-password"
            required
            disabled={!token}
          />
          <SubmitButton pending={pending} disabled={!token}>
            {t('auth.update_password', { defaultValue: 'Update password' })}
          </SubmitButton>
        </form>
      )}
    </AuthLayout>
  );
}
